import type { NavigateFunction } from "react-router-dom";
import type { useDispatch } from "react-redux";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { SavedCommodity } from "@freightsimple/generated-dashboard-openapi-client/models/SavedCommodity";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import type { UUID } from "@/Shared/types";
import type { useBookShipmentFinishBookingFlow } from "../Hooks/useBookShipmentFinishBookingFlow";
import {
  markFlowItemCompleted,
  setCreditCardBrand,
  setMakeDeliveryLocationAFavourite,
  setMakePickupLocationAFavourite,
} from "@/Slices/bookShipmentSlice";
import type {
  BookShipmentShipmentType,
  ShipmentDraftState,
} from "@/Shared/Types/bookShipmentSliceTypes";
import { bookShipmentGetNextInFlow } from "../Helpers/bookShipmentGetNextInFlow";
import { bookShipmentIsInFlow } from "../Helpers/bookShipmentIsInFlow";
import BookShipmentBrokerScreen from "../BookShipmentBrokerScreen";
import { BookShipmentDangerousGoodsScreen } from "../BookShipmentDangerousGoodsScreen";
import BookShipmentDeliveryAddressScreen from "../BookShipmentDeliveryAddressScreen";
import BookShipmentDetailsScreen from "../BookShipmentDetailsScreen";
import { BookShipmentDraftScreen } from "../BookShipmentDraftScreen";
import { BookShipmentNearlyDoneScreen } from "../BookShipmentNearlyDoneScreen";
import BookShipmentPaymentScreen from "../BookShipmentPaymentScreen";
import BookShipmentPickupAddressScreen from "../BookShipmentPickupAddressScreen";
import { BookShipmentProcessingScreen } from "../BookShipmentProcessingScreen";
import BookShipmentQuotesScreen from "../BookShipmentQuotesScreen";
import BookShipmentReviewScreen from "../BookShipmentReviewScreen";

interface BookShipmentRouterRoutesContext {
  shipment: BookShipmentShipmentType;
  loadingShipment: boolean;
  makePickupLocationAFavourite: boolean | undefined;
  makeDeliveryLocationAFavourite: boolean | undefined;
  draft: ShipmentDraftState | undefined;
  savedLocations: Array<SavedLocation>;
  savedCommodities: Array<SavedCommodity>;
  shipmentId: string | undefined;
  shipmentDraftId: string | undefined;
  defaultPickupLocationId: UUID | undefined;
  defaultDeliveryLocationId: UUID | undefined;
  finishBookingFlow: ReturnType<typeof useBookShipmentFinishBookingFlow>;
  dispatch: ReturnType<typeof useDispatch>;
  navigate: NavigateFunction;
  onModify: () => void;
  onPrevious: (flowIdentifier: string) => Promise<void>;
  onNavigate: (
    currentFlowIdentifier: string,
    proposedFlowIdentifier: string,
  ) => Promise<void>;
}

/**
 * The booking flow's route table: which screen answers each /book/<step> url.
 *
 * Two shapes, picked by what the url carries. A shipmentId means a real quoted
 * shipment, so every step from details through to nearly-done is reachable. A
 * shipmentDraftId means nothing has been quoted yet, so only the draft screen
 * exists.
 *
 * Lives apart from the router because the router's job is deciding what chrome
 * to draw around whichever screen this picks; interleaving the two made both
 * hard to follow.
 */
export function bookShipmentRouterRoutes(ctx: BookShipmentRouterRoutesContext) {
  const {
    shipment,
    loadingShipment,
    makePickupLocationAFavourite,
    makeDeliveryLocationAFavourite,
    draft,
    savedLocations,
    savedCommodities,
    shipmentId,
    shipmentDraftId,
    defaultPickupLocationId,
    defaultDeliveryLocationId,
    finishBookingFlow,
    dispatch,
    navigate,
    onModify,
    onPrevious,
    onNavigate,
  } = ctx;

  if (!savedLocations) {
    return [];
  }

  if (shipmentId) {
    const routes = [];

    routes.push({
      identifier: "details",
      path: `/book/details`,
      Component: (
        <BookShipmentDetailsScreen
          savedLocations={savedLocations}
          savedCommodities={savedCommodities}
          defaultPickupLocationId={defaultPickupLocationId}
          defaultDeliveryLocationId={defaultDeliveryLocationId}
        />
      ),
    });

    routes.push({
      identifier: "quotes",
      path: `/book/quotes`,
      Component: (
        <BookShipmentQuotesScreen
          shipment={shipment as PreBookingShipment}
          savedLocations={savedLocations}
          defaultPickupLocationId={defaultPickupLocationId}
          defaultDeliveryLocationId={defaultDeliveryLocationId}
        />
      ),
    });
    routes.push({
      identifier: "pickup-address",
      path: "/book/pickup-address",
      Component: (
        <BookShipmentPickupAddressScreen
          shipment={shipment as PreBookingShipment}
          savedLocations={savedLocations}
          loading={loadingShipment}
          onPrevious={async function () {
            await onNavigate("pickup-address", "quotes");
          }}
          onComplete={() => dispatch(markFlowItemCompleted("pickup-address"))}
          markLocationAsFavourite={makePickupLocationAFavourite}
          onMarkLocationAsFavourite={function (newValue: boolean) {
            dispatch(setMakePickupLocationAFavourite(newValue));
          }}
          onModify={onModify}
        />
      ),
    });

    routes.push({
      identifier: "delivery-address",
      path: "/book/delivery-address",
      Component: (
        <BookShipmentDeliveryAddressScreen
          shipment={shipment as PreBookingShipment}
          savedLocations={savedLocations}
          loading={loadingShipment}
          onComplete={() => dispatch(markFlowItemCompleted("delivery-address"))}
          onPrevious={async function () {
            await onPrevious("delivery-address");
          }}
          nextIdentifier={bookShipmentGetNextInFlow(
            finishBookingFlow,
            "delivery-address",
          )}
          markLocationAsFavourite={makeDeliveryLocationAFavourite}
          onMarkLocationAsFavourite={function (newValue: boolean) {
            dispatch(setMakeDeliveryLocationAFavourite(newValue));
          }}
          onModify={onModify}
        />
      ),
    });

    if (bookShipmentIsInFlow(finishBookingFlow, "broker")) {
      routes.push({
        identifier: "broker",
        path: "/book/broker",
        Component: (
          <BookShipmentBrokerScreen
            defaultValues={draft?.broker}
            shipment={shipment as PreBookingShipment}
            onComplete={() => dispatch(markFlowItemCompleted("broker"))}
            onPrevious={async function () {
              await onPrevious("broker");
            }}
            nextIdentifier={bookShipmentGetNextInFlow(
              finishBookingFlow,
              "broker",
            )}
          />
        ),
      });
    }

    if (bookShipmentIsInFlow(finishBookingFlow, "dangerous-goods")) {
      routes.push({
        identifier: "dangerous-goods",
        path: "/book/dangerous-goods",
        Component: (
          <BookShipmentDangerousGoodsScreen
            shipment={shipment as PreBookingShipment}
            onComplete={() =>
              dispatch(markFlowItemCompleted("dangerous-goods"))
            }
            onPrevious={async function () {
              await onPrevious("dangerous-goods");
            }}
            nextIdentifier={bookShipmentGetNextInFlow(
              finishBookingFlow,
              "dangerous-goods",
            )}
          />
        ),
      });
    }

    if (bookShipmentIsInFlow(finishBookingFlow, "payment")) {
      routes.push({
        identifier: "payment",
        path: "/book/payment",
        Component: (
          <BookShipmentPaymentScreen
            shipment={shipment as PreBookingShipment}
            onComplete={() => dispatch(markFlowItemCompleted("payment"))}
            onBrandChange={function (brand) {
              dispatch(setCreditCardBrand(brand));
            }}
            onPrevious={async function () {
              await onPrevious("payment");
            }}
            nextIdentifier={bookShipmentGetNextInFlow(
              finishBookingFlow,
              "payment",
            )}
          />
        ),
      });
    }
    routes.push({
      identifier: "review",
      path: "/book/review",
      Component: (
        <BookShipmentReviewScreen
          shipment={shipment as PreBookingShipment}
          savedLocations={savedLocations}
          onModifyDetails={onModify}
          onModifyPickupAddress={function () {
            navigate(`/book/pickup-address?shipmentId=${shipmentId}`);
          }}
          onModifyDeliveryAddress={function () {
            navigate(`/book/delivery-address?shipmentId=${shipmentId}`);
          }}
          onSelectAnotherQuote={function () {
            navigate(`/book/quotes?shipmentId=${shipmentId}`);
          }}
        />
      ),
    });

    routes.push({
      identifier: "processing",
      path: "/book/processing",
      Component: <BookShipmentProcessingScreen shipmentId={shipmentId} />,
    });

    routes.push({
      identifier: "nearlydone",
      path: "/book/nearlydone",
      Component: <BookShipmentNearlyDoneScreen shipmentId={shipmentId} />,
    });

    return routes;
  } else if (shipmentDraftId) {
    const routes = [
      {
        identifier: "draft",
        path: `/book/draft`,
        Component: (
          <BookShipmentDraftScreen
            shipmentDraftId={shipmentDraftId}
            savedLocations={savedLocations}
            savedCommodities={savedCommodities}
            defaultPickupLocationId={defaultPickupLocationId}
            defaultDeliveryLocationId={defaultDeliveryLocationId}
          />
        ),
      },
    ];
    return routes;
  } else {
    const routes = [
      {
        identifier: "details",
        path: `/book/details`,
        Component: (
          <BookShipmentDetailsScreen
            savedLocations={savedLocations}
            savedCommodities={savedCommodities}
            defaultPickupLocationId={defaultPickupLocationId}
            defaultDeliveryLocationId={defaultDeliveryLocationId}
          />
        ),
      },
    ];
    return routes;
  }
}
